<template>
  <v-menu
    v-model="editing"
    :close-on-content-click="false"
    :disabled="disabled || !editable"
  >
    <template #activator="{ props: activator }">
      <v-chip
        v-bind="editable ? activator : {}"
        :model-value="true"
        density="compact"
        color="primary"
        :closable="removable"
        :close-label="`Remove discount ${discount.name} from ${lineLabel}`"
        :disabled="disabled"
        :prepend-icon="editable ? 'mdi-pencil' : undefined"
        :role="editable ? 'button' : undefined"
        :tabindex="editable && !disabled ? 0 : undefined"
        :aria-label="
          editable
            ? `Edit discount ${discount.name} on ${lineLabel}`
            : undefined
        "
        @click:close="emit('remove')"
        >{{ discount.name
        }}<template v-if="editable"
          >&nbsp;({{ currentTerms }})</template
        ></v-chip
      >
    </template>
    <v-card min-width="280" :title="`${discount.name} on ${lineLabel}`">
      <v-form @submit.prevent="save">
        <v-card-text>
          <v-btn-toggle
            v-if="allowsAmount && allowsPercent"
            v-model="mode"
            mandatory
            density="compact"
            color="primary"
            class="mb-3"
          >
            <v-btn value="amount">Amount</v-btn>
            <v-btn value="percent">Percent</v-btn>
          </v-btn-toggle>
          <CurrencyInput
            v-if="mode === 'amount'"
            v-model:amount-cents="cents"
            label="Discount amount"
          />
          <v-text-field
            v-else
            v-model="percent"
            label="Discount percent"
            suffix="%"
            type="number"
            min="0"
            max="100"
            step="0.01"
            hide-details="auto"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="editing = false">Cancel</v-btn>
          <v-btn color="primary" type="submit" :disabled="!valid">Apply</v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-menu>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { DiscountType } from "../../../cbi-generator/generated/dto/DiscountType";
import type { ReceiptLineDetails } from "@/composables/shop/receiptLineDetails";
import CurrencyInput from "@/components/shared/CurrencyInput.vue";

export interface DiscountTerms {
  specifiedCents: number | null;
  specifiedPercent: string | null;
}

const props = defineProps<{
  discount: ReceiptLineDetails["discounts"][number];
  /** The catalog's discount type for this line; VARIABLE types are editable. */
  catalogType: DiscountType | null;
  lineLabel: string;
  removable: boolean;
  disabled: boolean;
}>();
const emit = defineEmits<{ remove: []; update: [terms: DiscountTerms] }>();

const allowsAmount = computed(
  () =>
    props.catalogType === DiscountType.VARIABLE ||
    props.catalogType === DiscountType.VARIABLE_AMOUNT,
);
const allowsPercent = computed(
  () =>
    props.catalogType === DiscountType.VARIABLE ||
    props.catalogType === DiscountType.VARIABLE_PERCENT,
);
const editable = computed(
  () =>
    !!props.discount.discountId && (allowsAmount.value || allowsPercent.value),
);
const editing = ref(false);
const mode = ref<"amount" | "percent">("amount");
const cents = ref<number>();
const percent = ref("");
const money = (value: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    value / 100,
  );
const currentTerms = computed(() =>
  props.discount.discountType === DiscountType.VARIABLE_PERCENT
    ? `${props.discount.specifiedPercent ?? "0"}%`
    : money(props.discount.specifiedCents ?? 0),
);
const valid = computed(() => {
  if (mode.value === "amount")
    return (
      cents.value != null &&
      Number.isSafeInteger(cents.value) &&
      cents.value >= 0
    );
  const value = Number(percent.value);
  return percent.value.trim() !== "" && value >= 0 && value <= 100;
});

watch(editing, (open) => {
  if (!open) return;
  mode.value =
    props.discount.discountType === DiscountType.VARIABLE_PERCENT ||
    !allowsAmount.value
      ? "percent"
      : "amount";
  cents.value = props.discount.specifiedCents ?? undefined;
  percent.value = props.discount.specifiedPercent?.replace("%", "") ?? "";
});

function save() {
  if (!valid.value) return;
  emit(
    "update",
    mode.value === "amount"
      ? { specifiedCents: cents.value!, specifiedPercent: null }
      : { specifiedCents: null, specifiedPercent: percent.value.trim() },
  );
  editing.value = false;
}
</script>
